import React, { useEffect, useState } from 'react';
import {
  fetchReferentialUsage,
  deprecateReferentialItem,
  type RefTable,
  type ReferentialUsage,
} from '../lib/referentialService';

/** Libellés lisibles des compteurs renvoyés par `referential_usage`. */
const USAGE_LABELS: Record<string, string> = {
  subsectors: 'sous-secteurs',
  positions: 'postes',
  skill_groups: 'groupes de compétences',
  skills: 'compétences',
  certifications: 'certifications',
  candidate_experiences: 'expériences candidat',
  candidate_diplomas: 'diplômes candidat',
  job_offers: 'offres',
  video_tags: 'tags vidéo',
  skill_families: 'familles de compétences',
};

function usageLines(counts: Record<string, number>): string[] {
  return Object.entries(counts)
    .filter(([, n]) => Number(n) > 0)
    .map(([key, n]) => `${n} ${USAGE_LABELS[key] || key}`);
}

interface DeprecateDialogProps {
  table: RefTable;
  id: string;
  name: string;
  /** Libellé accordé, au format « le poste », « la certification ». */
  label: string;
  onCancel: () => void;
  onConfirmed: () => void | Promise<void>;
}

/**
 * Récapitulatif d'impact avant mise en obsolescence. Le référentiel ne se
 * supprime jamais : l'admin doit voir ce qui reste rattaché avant de décider,
 * et choisir explicitement de descendre la cascade.
 */
const DeprecateDialog: React.FC<DeprecateDialogProps> = ({ table, id, name, label, onCancel, onConfirmed }) => {
  const [usage, setUsage] = useState<ReferentialUsage | null>(null);
  const [cascade, setCascade] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUsage(null);
    setError(null);
    fetchReferentialUsage(table, id)
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); });
    return () => { cancelled = true; };
  }, [table, id]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await deprecateReferentialItem(table, id, cascade);
      await onConfirmed();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const dataLines = usage ? usageLines(usage.usage) : [];
  const childLines = usage ? usageLines(usage.children) : [];

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={() => !busy && onCancel()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Rendre {label} obsolète</h3>
          <p className="text-sm text-gray-500 mt-0.5">{name}</p>
        </div>

        <div className="p-6 space-y-4">
          {!usage && !error && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="w-4 h-4 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin" />
              Calcul des impacts...
            </div>
          )}

          {usage && (
            <>
              {dataLines.length > 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 mb-1">Déjà utilisé par</p>
                  <ul className="text-sm text-blue-800 list-disc list-inside">
                    {dataLines.map((line) => <li key={line}>{line}</li>)}
                  </ul>
                  <p className="text-xs text-blue-700 mt-2">
                    Ces données restent intactes et continueront de s'afficher.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Aucune donnée utilisateur ne référence cet élément.</p>
              )}

              {childLines.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-800 mb-1">Contient</p>
                  <ul className="text-sm text-gray-600 list-disc list-inside">
                    {childLines.map((line) => <li key={line}>{line}</li>)}
                  </ul>
                  <label className="flex items-start gap-2 mt-3 text-sm text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={cascade}
                      onChange={(e) => setCascade(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-[#00244F] focus:ring-[#00244F]/20"
                    />
                    <span>
                      Rendre obsolète tout le contenu
                      <span className="block text-xs text-gray-400">
                        Sinon seul {label} disparaît des listes de saisie ; son contenu reste actif mais devient inaccessible.
                      </span>
                    </span>
                  </label>
                </div>
              )}
            </>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors disabled:opacity-40"
          >
            Annuler
          </button>
          <button
            onClick={confirm}
            disabled={busy || !usage}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? 'En cours...' : 'Rendre obsolète'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeprecateDialog;
